import { ISearchSuggestionProps } from '@1js/search-types';
import { NgZone } from '@angular/core';
import React from 'react';
import { SearchConfigService } from '../../search-results/services/search-config.service';
import { OfficeRoute } from '../../shared/constants/office-route.enum';
import { ClassNames } from '../../shared/icon-font/icon-font-classes.utility';
import { IconFontType } from '../../shared/icon-font/icon-font-type.enum';
import { FileItem } from '../../shared/models/file-item.model';
import { CreateDocumentResult, CreateDocumentService } from '../../shared/services/create-document.service';
import { DeviceDetector } from '../../shared/services/device-detector.service';
import { OfficeRouteService } from '../../shared/services/office-route.service';
import { LaunchAction, UrlLauncherService } from '../../shared/services/url-launcher.service';
import { Template } from '../../shared/templates/templates.model';
import { SearchInstrumentationService, SearchResultElementId, SearchResultType } from '../search-instrumentation.service';
import { SearchSuggestionGalleryItemComponent } from './search-suggestion-components/search-suggestion-gallery/search-suggestion-galleryItem.component';
import { TemplateSuggestionProvider } from './template.search-suggestion-provider';
import { TemplateSuggestionComponent } from './template.search-suggestion-renderer';

export interface TemplatePreviewItem extends Template {
    readonly application: string;
    readonly imageHeight: number;
    readonly imageWidth: number;
    readonly imageUrl: string;
    readonly position: string;
}

interface TemplatePreviewProps extends ISearchSuggestionProps {
    readonly createDocumentService: CreateDocumentService;
    readonly deviceDetector: DeviceDetector;
    readonly isDarkMode: boolean;
    readonly isEndOfResult: boolean;
    readonly isMSA: boolean;
    readonly isPwa: boolean;
    readonly ngZone: NgZone;
    readonly officeRouteService: OfficeRouteService;
    readonly searchConfigService: SearchConfigService;
    readonly searchInstrumentationService: SearchInstrumentationService;
    readonly searchText: string;
    readonly templatePreviewData: TemplatePreviewItem;
    readonly urlLauncherService: UrlLauncherService;
    readonly seeMoreTemplateUrl?: string;
}

const providerName = 'templatePreview';

const styles = {
    lineHeight: '0px',
    position: 'relative',
    display: 'inline-block',
    width: '31%',
    marginLeft: '2.5%',
    marginRight: '1%',
    offsetHeight: 5
};

export class TemplatePreviewSuggestionComponent extends React.Component<TemplatePreviewProps> {

    public readonly handleClick = () => (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        const fileType = FileItem.fileTypeNameToFileType(this.props.templatePreviewData.application);
        const referral = FileItem.referralFromFileType(fileType);
        this.props.createDocumentService.createItem(fileType, null, this.props.templatePreviewData, (_: CreateDocumentResult) => {
            this.props.searchInstrumentationService.logSearchClickEvent(
                                                        SearchResultType.Template,
                                                        SearchResultElementId.Template,
                                                        true,
                                                        SearchResultType.Template,
                                                        this.props.templatePreviewData.AssetId,
                                                        referral,
                                                        undefined,
                                                        this.props.templatePreviewData.position);
        });
    }

    public readonly handleSeeMoreTemplateClick = () => (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (this.props.deviceDetector.isMobileDevice() || this.props.searchConfigService.config.disableSearchEnterPressedAction || !this.props.isMSA) {
            this.props.urlLauncherService.openUrl(this.props.seeMoreTemplateUrl, LaunchAction.OpenAutosuggestTemplate).then(() => {
                this.logSeeMoreClickEvent();
            });
        } else {
            this.logSeeMoreClickEvent();
            this.props.ngZone.run(() => this.props.officeRouteService.navigateTo(
                { route: OfficeRoute.ConsumerTemplateSearch },
                false,
                { q: this.props.searchText }
            ));
        }
    }

    public render() {
        const iconClass = ClassNames.FluentIconCssClass;
        const iconSVGClass = ClassNames.SvgIconCssClass;

        if (!this.props.isEndOfResult) {
            if (this.props.searchConfigService.config.templatePreviewProviderWithNoThumbnails) {
                return (
                    <div>
                        {
                            this.props.templatePreviewData !== undefined &&
                            this.props.templatePreviewData !== null &&
                            <TemplateSuggestionComponent
                                displayText={this.props.displayText}
                                handleClick={this.handleClick()}
                                id={this.props.id}
                                isDarkMode={this.props.isDarkMode}
                                isPwa={this.props.isPwa}
                                searchInstrumentationService={this.props.searchInstrumentationService}
                                searchText={this.props.searchText}
                                title={this.props.templatePreviewData.Title + ' - ' + this.props.templatePreviewData.application}
                                url={this.props.url}
                                urlLauncherService={this.props.urlLauncherService}
                                urlToOpen={TemplateSuggestionProvider.getTemplateURL(this.props.searchText)}
                            />
                        }
                    </div>
                );
            } else {
                return (
                    <div id={this.props.id} >
                        {
                            this.props.templatePreviewData !== undefined &&
                            this.props.templatePreviewData !== null &&
                            <SearchSuggestionGalleryItemComponent
                                application={this.props.templatePreviewData.application}
                                icon={iconSVGClass + ' ' +
                                    iconClass + ' ' +
                                    ClassNames.getCssClass(IconFontType.Diamond) +
                                    (this.props.isDarkMode && this.props.isPwa ? ' search-Icon--darkMode' : '')}
                                imageUrl={this.props.templatePreviewData.imageUrl}
                                isPremium={this.props.templatePreviewData.IsPremium}
                                key={this.props.templatePreviewData.position}
                                title={this.props.templatePreviewData.Title}
                                handleTemplateClick={this.handleClick()}
                                isDarkMode={this.props.isDarkMode}
                                isPwa={this.props.isPwa}
                                target='_blank'
                            />
                        }
                    </div>
                );
            }
        } else {
            return (
                <TemplateSuggestionComponent
                    displayText={this.props.displayText}
                    handleClick={this.handleSeeMoreTemplateClick()}
                    id={this.props.id}
                    isDarkMode={this.props.isDarkMode}
                    isPwa={this.props.isPwa}
                    searchInstrumentationService={this.props.searchInstrumentationService}
                    searchText={this.props.searchText}
                    style={this.props.searchConfigService.config.templatePreviewProviderWithNoThumbnails ? '' : 'templatePreviewSeeMoreButton'}
                    title={TemplateSuggestionProvider.seeMoreButtonText(this.props.searchText)}
                    url={this.props.url}
                    urlLauncherService={this.props.urlLauncherService}
                    urlToOpen={TemplateSuggestionProvider.getTemplateURL(this.props.searchText)}
                />
            );
        }
    }

    public componentDidMount() {
        if (!this.props.searchConfigService.config.templatePreviewProviderWithNoThumbnails) {
            if (this.props.isEndOfResult) {
                const templatePreviewContainer = document.getElementById(providerName);
                (templatePreviewContainer.parentNode as HTMLElement).style.lineHeight = styles.lineHeight;
            } else {
                const parent = document.getElementById(providerName + (parseInt(this.props.templatePreviewData.position) - 1)).parentElement;
                parent.style.width = styles.width;
                parent.style.display = styles.display;

                if (this.props.templatePreviewData.position === '1') {
                    parent.style.marginLeft = styles.marginLeft;
                    parent.style.marginRight = styles.marginRight;
                } else if (this.props.templatePreviewData.position === '2') {
                    parent.style.marginRight = styles.marginRight;
                }
            }
        }
    }

    private logSeeMoreClickEvent() {
        this.props.searchInstrumentationService.logSearchClickEvent(
            SearchResultType.Template,
            SearchResultElementId.Template,
            true,
            SearchResultType.SeeMore /* See more result in template provider*/);
    }
}
